import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Modal, Select, Input, Progress } from "antd";
import { connect } from "react-redux";
import { Api, Notification } from "services";
import { Button } from "components/Button";
import {
  postCreateTopic,
  uploadlecturenotes,
  removemodel,
} from "actions/digitalLibrary";

const emptyBatchState = () => ({
  chapters: [],
  chaptersLoading: false,
  chapterId: null,
  chapterName: "",
  topics: [],
  topicsLoading: false,
  topicId: null,
  topicSearch: "",
  topicDropdownOpen: false,
});

const SaveToLibraryModal = ({
  visible,
  onClose,
  file,
  pageCount,
  subjectId,
  subjectName,
  standards = [],
  occurrenceId,
  postCreateTopic,
  uploadlecturenotes,
  removemodel,
}) => {
  const [batchState, setBatchState] = useState({});
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingLabel, setUploadingLabel] = useState("");
  const topicWrapperRefs = useRef({});

  const moveTopicCursorToEnd = (standardId) => {
    const input = topicWrapperRefs.current[standardId]?.querySelector("input");
    if (input) {
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  };

  const patchBatch = (standardId, patch) => {
    setBatchState((prev) => ({
      ...prev,
      [standardId]: { ...emptyBatchState(), ...prev[standardId], ...patch },
    }));
  };

  const fetchChaptersForBatch = (standardId) => {
    patchBatch(standardId, { chaptersLoading: true });
    const url = `/admin-library/filterChapter?name=&standardId=${standardId}&subjectId=${subjectId || ""}`;
    Api.get(url).send((response) => {
      patchBatch(standardId, {
        chapters: Array.isArray(response) ? response : [],
        chaptersLoading: false,
      });
    });
  };

  const fetchTopicsForBatch = (standardId, chapterId, onLoaded) => {
    patchBatch(standardId, { topicsLoading: true });
    const url = `/admin-library/filterTopic?subjectId=${subjectId || ""}&standardId=${standardId}&chapterId=${chapterId}`;
    Api.get(url).send((response) => {
      const topics = Array.isArray(response) ? response : [];
      patchBatch(standardId, { topics, topicsLoading: false });
      if (onLoaded) onLoaded(topics);
    });
  };

  useEffect(() => {
    if (visible) {
      setTitle("");
      setUploading(false);
      setUploadProgress(0);
      setUploadingLabel("");
      const initial = {};
      standards.forEach((s) => {
        initial[s.id] = emptyBatchState();
      });
      setBatchState(initial);
      standards.forEach((s) => {
        if (subjectId && s.id) {
          fetchChaptersForBatch(s.id);
        }
      });
    }
  }, [visible]);

  const handleChapterChange = (standardId) => (value, option) => {
    patchBatch(standardId, {
      chapterId: value,
      chapterName: option?.label || "",
      topicId: null,
      topicSearch: "",
      topics: [],
    });
    fetchTopicsForBatch(standardId, value);
  };

  const clearTopicAndOpenDropdown = (standardId) => {
    patchBatch(standardId, { topicId: null, topicSearch: "" });
    // The clear icon's own click handling closes the dropdown right after this
    // (calling onDropdownVisibleChange(false) synchronously), which would
    // otherwise overwrite an "open" set here. Defer so ours applies last.
    setTimeout(() => patchBatch(standardId, { topicDropdownOpen: true }), 0);
  };

  const handleTopicChange = (standardId) => (value) => {
    if (value === undefined || value === null) {
      clearTopicAndOpenDropdown(standardId);
    } else {
      patchBatch(standardId, { topicId: value, topicSearch: "" });
    }
  };

  // Root cause: when showSearch is on, antd writes the chosen option's label
  // straight into the underlying <input> so it stays editable/searchable, and
  // that write leaves the caret at position 0 instead of the end. Any state
  // change that rewrites the input's text (a selection, a created topic, or a
  // keystroke) re-renders this component, so a single useLayoutEffect keyed
  // on that text is enough to correct the caret right after each such render
  // commits and before the browser paints — no timers, no race with arrow-key
  // navigation (which never touches this state, so the effect stays idle).
  const topicCursorSyncKey = standards
    .map((s) => `${s.id}:${batchState[s.id]?.topicId ?? ""}:${batchState[s.id]?.topicSearch ?? ""}`)
    .join("|");
  useLayoutEffect(() => {
    standards.forEach((s) => moveTopicCursorToEnd(s.id));
  }, [topicCursorSyncKey]);

  // Refocusing an already-filled field doesn't change topicId/topicSearch, so
  // the layout effect above won't re-run; nudge the caret on the next frame
  // instead, once any focus-time re-render antd does has settled.
  const handleTopicFocus = (standardId) => () => {
    requestAnimationFrame(() => moveTopicCursorToEnd(standardId));
  };

  const handleTopicSearch = (standardId) => (value) => {
    patchBatch(standardId, { topicSearch: value });
  };

  const handleCreateTopic = (standardId) => () => {
    const batch = batchState[standardId] || emptyBatchState();
    if (!batch.chapterId || !batch.topicSearch) {
      Notification.error("error", "Please select a chapter and enter a topic name");
      return;
    }
    const createdName = batch.topicSearch;
    postCreateTopic(
      { chapterId: batch.chapterId, topicName: createdName },
      () => {},
      () => {
        fetchTopicsForBatch(standardId, batch.chapterId, (topics) => {
          const created = topics.find(
            (t) => (t.name || t.topicName || "").toLowerCase() === createdName.toLowerCase()
          );
          patchBatch(standardId, {
            topicSearch: "",
            topicId: created ? created.id || created.topicId : null,
          });
        });
      }
    );
  };

  const isFormValid =
    standards.length > 0 &&
    standards.every((s) => batchState[s.id]?.chapterId && batchState[s.id]?.topicId) &&
    title.trim() &&
    file;

  const uploadForBatch = (standard) =>
    new Promise((resolve) => {
      const batch = batchState[standard.id] || emptyBatchState();
      let succeeded = false;
      const uploadObject = {
        subjectId,
        standardId: parseInt(standard.id),
        standardIds: JSON.stringify([parseInt(standard.id)]),
        chapterId: parseInt(batch.chapterId),
        chapterName: batch.chapterName,
        topicId: batch.topicId,
        title: title.trim(),
        type: "lecture-notes",
        file,
        occurrenceId,
        occuranceId: occurrenceId,
        documentPages: pageCount,
        videoDuration: 0,
        size: file?.size,
      };
      uploadlecturenotes(
        uploadObject,
        // On the success path this "finally" callback fires BEFORE successCallback below,
        // so defer resolution to let successCallback flip `succeeded` first.
        () => setTimeout(() => resolve(succeeded), 0),
        () => {
          succeeded = true;
        },
        (percent) => setUploadProgress(percent)
      );
    });

  const handleSave = async () => {
    if (!isFormValid || uploading) {
      return;
    }
    setUploading(true);
    const results = [];
    for (const standard of standards) {
      setUploadingLabel(standard.label || "");
      setUploadProgress(0);
      const ok = await uploadForBatch(standard);
      results.push({ standard, ok });
    }
    setUploading(false);
    setUploadProgress(0);
    setUploadingLabel("");
    // uploadlecturenotes flips a global "uploadsuccessful" flag that the plain
    // Digital Library pages use to pop their own "Successfully published!"
    // modal. Clear it so it doesn't surface later when the user visits the
    // Digital Library from this broadcast save flow.
    removemodel();

    const failed = results.filter((r) => !r.ok);
    if (failed.length === 0) {
      Notification.success("success", "Slides saved to library successfully");
      onClose();
    } else if (failed.length === results.length) {
      Notification.error("error", "Failed to save slides to library");
    } else {
      Notification.error(
        "error",
        `Saved to ${results.length - failed.length}/${results.length} batches. Failed: ${failed
          .map((f) => f.standard.label || f.standard.id)
          .join(", ")}`
      );
      onClose();
    }
  };

  return (
    <Modal
      centered
      width={520}
      visible={visible}
      footer={null}
      onCancel={() => {
        if (!uploading) {
          onClose();
        }
      }}
      closable={!uploading}
      maskClosable={false}
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
    >
      <div style={{ fontSize: 18, fontWeight: "bold", color: "#191919" }}>
        Save to Library
      </div>
      <div style={{ color: "#8c8c8c", fontSize: 13, marginBottom: 15 }}>
        {pageCount} page{pageCount === 1 ? "" : "s"} will be saved as a PDF
      </div>

      {subjectName && (
        <div
          style={{
            fontSize: 12,
            color: "#595959",
            backgroundColor: "#F6F4FE",
            borderRadius: 8,
            padding: "8px 12px",
            marginBottom: 15,
          }}
        >
          Saving to: {subjectName}
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Title</div>
        <Input
          placeholder="Enter a title for this resource"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={uploading}
        />
      </div>

      {standards.map((standard) => {
        const batch = batchState[standard.id] || emptyBatchState();
        const chapterOptions = (batch.chapters || [])
          .filter(Boolean)
          .map((item) => ({
            value: item.id || item.chapterId || item.chapterid,
            label: item.name || item.chapterName,
          }));
        const topicOptions = (batch.topics || []).filter(Boolean).map((item) => ({
          value: item.id || item.topicId,
          label: item.name || item.topicName,
        }));
        const showCreateTopicOption =
          batch.topicSearch &&
          !topicOptions.some(
            (opt) => opt.label?.toLowerCase() === batch.topicSearch.toLowerCase()
          );

        return (
          <div
            key={standard.id}
            style={{
              border: "1px solid #E6E6E6",
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "#191919", marginBottom: 8 }}>
              {standard.label || `Batch ${standard.id}`}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Chapter</div>
              <Select
                showSearch
                style={{ width: "100%" }}
                placeholder="Select chapter"
                value={batch.chapterId || undefined}
                options={chapterOptions}
                loading={batch.chaptersLoading}
                filterOption={(input, option) =>
                  (option?.label || "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleChapterChange(standard.id)}
                disabled={uploading}
              />
            </div>

            <div ref={(el) => { topicWrapperRefs.current[standard.id] = el; }}>
              <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>Topic</div>
              <Select
                showSearch
                allowClear
                style={{ width: "100%" }}
                placeholder={batch.chapterId ? "Select or create a topic" : "Select a chapter first"}
                value={batch.topicId || undefined}
                disabled={!batch.chapterId || uploading}
                loading={batch.topicsLoading}
                open={batch.topicDropdownOpen}
                onDropdownVisibleChange={(open) => patchBatch(standard.id, { topicDropdownOpen: open })}
                onSearch={handleTopicSearch(standard.id)}
                filterOption={(input, option) =>
                  (option?.label || "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleTopicChange(standard.id)}
                onClear={() => clearTopicAndOpenDropdown(standard.id)}
                onFocus={handleTopicFocus(standard.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && showCreateTopicOption) {
                    e.preventDefault();
                    handleCreateTopic(standard.id)();
                    return;
                  }
                  if (e.key === "Backspace" && batch.topicId) {
                    e.preventDefault();
                    clearTopicAndOpenDropdown(standard.id);
                  }
                }}
                options={topicOptions}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {showCreateTopicOption && (
                      <div
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleCreateTopic(standard.id)}
                        style={{
                          borderTop: "1px solid #E6E6E6",
                          padding: "8px 12px",
                          fontSize: 12,
                          color: "#594099",
                          cursor: "pointer",
                          fontWeight: 500,
                        }}
                      >
                        + Create "{batch.topicSearch}"
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>
        );
      })}

      {uploading && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>
            Saving to {uploadingLabel}...
          </div>
          <Progress percent={uploadProgress} />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button type="primary" loading={uploading} disabled={!isFormValid} onClick={handleSave}>
          Save
        </Button>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => ({
  postCreateTopic: (object, callback, successCallback) =>
    dispatch(postCreateTopic(object, callback, successCallback)),
  uploadlecturenotes: (object, callback, successCallback, setProgress) =>
    dispatch(uploadlecturenotes(object, callback, successCallback, setProgress)),
  removemodel: () => dispatch(removemodel()),
});

export default connect(null, mapDispatchToProps)(SaveToLibraryModal);
